/**
 * UnifiedProductPickerModal.tsx
 *
 * Warehouse-aware picker that prioritizes products already present in a warehouse.
 * Clearing the "na stanie" checkbox extends the results with the full product
 * catalog while preserving the last known warehouse location where available.
 */

// codex resume 019f3617-ccb0-7173-8ac4-86314ea73fd8

import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Text as ChakraText,
  Checkbox,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  ProductListApi,
  type ProductListItemDTO,
} from "../../../api/productList"
import {
  WareHouseService,
  type WarehouseResponceDTO,
  type WarehouseStockProductDTO,
} from "../../../client"
import { formatLotDate } from "../shared/warehouseLots"
import { universalSearch } from "../utils"

export { getInitialStockDocumentQty } from "../shared/stockDocumentDefaultQty"

const EMPTY_UUID = "00000000-0000-0000-0000-000000000000"

/** Props accepted by the UnifiedProductPickerModal component. */
export type UnifiedProductPickerModalProps = {
  /** Whether the modal is open. */
  isOpen: boolean
  /** Callback to close the modal. */
  onClose: () => void
  /**
   * Callback when a product row is selected.
   * Warehouse rows may include locationCode, lotId, and lotNo. Catalog-only
   * rows provide productId and product.
   */
  onChoose: (
    productId: string,
    locationCode?: string,
    lotId?: string,
    lotNo?: string,
    product?: ProductListItemDTO,
    productionDate?: string,
    expirationDate?: string,
    availableQty?: number,
  ) => void
  /** Optional title override for the modal header. */
  title?: string
  /** Warehouse whose current and historical stock locations are displayed. */
  warehouseId?: string
  /** Release ID used to resolve reservation context for WZ documents. */
  releaseId?: string
  /**
   * Map of `productId:locationCode:lotId` → qty already used in the current
   * document. The lot segment is empty for a regular stock row.
   */
  usedQtyByStockKey?: Record<string, number>
  /** Narrow results to a single product (e.g. for lot picking inside a row). */
  productIdFilter?: string
  /** Lot IDs to hard-disable (e.g. lots already added to the document). */
  disabledLotIds?: string[]
  /**
   * Product IDs to hard-disable. Supplementary to quantity-based blocking —
   * use sparingly; prefer usedQtyByStockKey for partial blocking.
   */
  disabledProductIds?: string[]
  /** When true, products with available ≤ 0 are still selectable. */
  allowOutOfStockSelection?: boolean
  /** When true, show a catalog-only picker without requiring a warehouse. */
  catalogOnly?: boolean
  /** Products that already exist in the current document or form. */
  highlightedProductIds?: string[]
  /** Exact warehouse rows already used in the current document (`product:location:lot`). */
  highlightedStockKeys?: string[]
  /** Enables multi-row selection with checkboxes and a bulk add button. */
  multiSelect?: boolean
  /** Callback for bulk selection when `multiSelect` is enabled. */
  onChooseMany?: (selections: PickerSelection[]) => void
}

/** Data returned for a single selected row. */
export type PickerSelection = {
  productId: string
  locationCode?: string
  lotId?: string
  lotNo?: string
  product?: ProductListItemDTO
  productionDate?: string
  expirationDate?: string
  /** Picker-visible availability for a warehouse row, after reservations. */
  availableQty?: number
}

/** Builds the stable key used to account for repeated selections of one stock row. */
export function buildProductPickerStockKey(
  productId: string,
  locationCode?: string,
  lotId?: string,
) {
  return `${productId}:${locationCode?.trim().toUpperCase() ?? ""}:${lotId?.trim() ?? ""}`
}

/** Converts the compact warehouse projection into the picker callback contract. */
function toProductListItem(
  product: WarehouseStockProductDTO,
  currentPriceNetto?: number,
): ProductListItemDTO {
  return {
    id: product.id,
    sku: product.sku,
    name: product.name,
    manufacturer: product.manufacturer,
    supplier: "",
    EAN: product.EAN,
    unit: product.unit,
    is_active: product.is_active,
    is_service: product.is_service,
    requires_batch_tracking: product.requires_batch_tracking,
    requires_expiration_date: product.requires_expiration_date,
    acronym: "",
    created_at: product.created_at,
    default_min_qty: product.default_min_qty,
    current_price_netto: currentPriceNetto,
  }
}

// ── Normalised picker row ──────────────────────────────────────────────────

/** Internal row shape used for rendering. */
type PickerRow = {
  productId: string
  lotId?: string
  lotNo?: string
  isWarehouseLot: boolean
  productionDate?: string
  expirationDate?: string
  sku: string
  name: string
  manufacturer: string
  unit: string
  qty_on_hand: number
  reserved_mm: number
  reserved_so: number
  locally_reserved: number
  available: number
  location_code?: string
  product: ProductListItemDTO
  isService: boolean
}

/** Renders the warehouse-first product picker used by document editors. */
export default function UnifiedProductPickerModal({
  isOpen,
  onClose,
  onChoose,
  title,
  warehouseId,
  releaseId,
  usedQtyByStockKey,
  productIdFilter,
  disabledLotIds,
  disabledProductIds,
  allowOutOfStockSelection = false,
  catalogOnly = false,
  highlightedProductIds,
  highlightedStockKeys,
  multiSelect = false,
  onChooseMany,
}: UnifiedProductPickerModalProps) {
  // ── Local state ────────────────────────────────────────────────────────

  const [query, setQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [inStockOnly, setInStockOnly] = useState(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    Record<string, boolean>
  >({})
  const [selectedRowsByKey, setSelectedRowsByKey] = useState<
    Record<string, PickerSelection>
  >({})

  const normalizedQuery = query.trim()

  // Empty and sentinel IDs mean that no warehouse has been selected yet.
  const normalizedWarehouseId =
    warehouseId?.trim() && warehouseId !== EMPTY_UUID ? warehouseId : undefined
  // A material-location picker already knows the exact product, but the
  // warehouse endpoint paginates before the client applies productIdFilter.
  // Load all stock rows in that mode so a location/lot after the first page
  // is still available for selection.
  const loadAllStockRows = Boolean(productIdFilter)

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("")
      setSelectedRowKeys({})
      setSelectedRowsByKey({})
    }
  }, [isOpen])

  // ── Theme tokens ───────────────────────────────────────────────────────

  const rowHoverBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const selectedRowBg = useColorModeValue("orange.50", "orange.900")
  const partiallyUsedRowBg = useColorModeValue("yellow.50", "yellow.900")

  // ── Data fetching ──────────────────────────────────────────────────────

  // Current or historically located stock rows for the selected warehouse.
  const {
    data: warehouseData,
    isPending: isStockPending,
    isError: isStockError,
    error: stockError,
  } = useQuery<WarehouseResponceDTO | undefined>({
    queryKey: [
      "wms",
      "warehouse",
      normalizedWarehouseId,
      {
        inStock: inStockOnly,
        releaseId,
        search: inStockOnly ? normalizedQuery : "",
        productIdFilter: productIdFilter ?? "",
      },
    ],
    queryFn: () =>
      normalizedWarehouseId
        ? WareHouseService.getWarehouseByID(
            normalizedWarehouseId,
            inStockOnly,
            releaseId,
            inStockOnly ? normalizedQuery || undefined : undefined,
            "all",
            inStockOnly && !loadAllStockRows ? 1 : undefined,
            inStockOnly && !loadAllStockRows ? 50 : undefined,
          )
        : Promise.resolve(undefined as any),
    enabled: isOpen && !!normalizedWarehouseId,
  })

  // Full catalog supplement used when "na stanie" is cleared or catalog mode is active.
  const {
    data: catalogResponse,
    isPending: isCatalogPending,
    isError: isCatalogError,
    error: catalogError,
  } = useQuery({
    queryKey: [
      "products",
      "picker",
      catalogOnly ? "catalog-only" : "warehouse-catalog",
      normalizedQuery,
    ],
    queryFn: () =>
      ProductListApi.getProductListPage({
        search: normalizedQuery || undefined,
        page: 1,
        page_size: 50,
        sort_by: "sku",
        sort_dir: "asc",
      }),
    enabled:
      isOpen && (catalogOnly || (!!normalizedWarehouseId && !inStockOnly)),
  })
  const stockCatalogRows = catalogResponse?.items ?? []

  // ── Row computation ────────────────────────────────────────────────────

  // Warehouse stock rows.
  const stockRows: PickerRow[] = useMemo(() => {
    const stocks = warehouseData?.stocks ?? []
    return stocks.map((s) => {
      const available =
        (s.qty_on_hand ?? 0) - (s.reserved_mm ?? 0) - (s.reserved_so ?? 0)
      const productId = s.product.id
      const locationCode = s.location_code ?? ""
      const key = buildProductPickerStockKey(productId, locationCode, s.lot_id)
      const locallyReserved = Math.max(0, Number(usedQtyByStockKey?.[key] ?? 0))
      const product = toProductListItem(s.product, s.current_price_netto)
      return {
        productId,
        lotId: s.lot_id,
        lotNo: s.lot_no,
        isWarehouseLot: Boolean(s.lot_id),
        productionDate: s.production_date,
        expirationDate: s.expiration_date,
        sku: product.sku,
        name: product.name,
        manufacturer: product.manufacturer,
        unit: product.unit,
        qty_on_hand: s.qty_on_hand ?? 0,
        reserved_mm: s.reserved_mm ?? 0,
        reserved_so: s.reserved_so ?? 0,
        locally_reserved: locallyReserved,
        available: available - locallyReserved,
        location_code: s.location_code,
        product,
        isService: product.is_service,
      }
    })
  }, [warehouseData, usedQtyByStockKey])

  // Catalog-only rows when "na stanie" is OFF.
  const catalogOnlyRows: PickerRow[] = useMemo(() => {
    if (inStockOnly) return []
    const stockProductIds = new Set(
      stockRows.map((row) => row.productId).filter(Boolean),
    )
    return stockCatalogRows
      .filter((product) => !stockProductIds.has(product.id))
      .map((product) => ({
        productId: product.id,
        lotId: undefined,
        lotNo: undefined,
        isWarehouseLot: false,
        productionDate: undefined,
        expirationDate: undefined,
        sku: product.sku,
        name: product.name,
        manufacturer: product.manufacturer,
        unit: product.unit,
        qty_on_hand: 0,
        reserved_mm: 0,
        reserved_so: 0,
        locally_reserved: 0,
        available: 0,
        location_code: "",
        product,
        isService: Boolean(product.is_service),
      }))
  }, [stockCatalogRows, inStockOnly, stockRows])

  // ── Filtering ──────────────────────────────────────────────────────────

  const filtered: PickerRow[] = useMemo(() => {
    if (catalogOnly) {
      return universalSearch(stockCatalogRows, normalizedQuery, {
        keys: ["sku", "name", "manufacturer", "unit"] as any,
      }).map((product) => ({
        productId: product.id,
        lotId: undefined,
        lotNo: undefined,
        isWarehouseLot: false,
        productionDate: undefined,
        expirationDate: undefined,
        sku: product.sku,
        name: product.name,
        manufacturer: product.manufacturer,
        unit: product.unit,
        qty_on_hand: 0,
        reserved_mm: 0,
        reserved_so: 0,
        locally_reserved: 0,
        available: 0,
        location_code: "",
        product,
        isService: Boolean(product.is_service),
      }))
    }

    if (inStockOnly) {
      const scopedRows = productIdFilter
        ? stockRows.filter((row) => row.productId === productIdFilter)
        : stockRows
      return scopedRows
    }
    const mergedRows = [...stockRows, ...catalogOnlyRows]
    const scopedRows = productIdFilter
      ? mergedRows.filter((row) => row.productId === productIdFilter)
      : mergedRows
    return universalSearch(scopedRows, normalizedQuery, {
      keys: ["sku", "name", "manufacturer", "unit"] as any,
    })
  }, [
    inStockOnly,
    stockRows,
    catalogOnlyRows,
    normalizedQuery,
    productIdFilter,
    catalogOnly,
    stockCatalogRows,
  ])

  const disabledLotIdSet = useMemo(
    () => new Set((disabledLotIds ?? []).filter(Boolean)),
    [disabledLotIds],
  )
  const disabledProductIdSet = useMemo(
    () => new Set((disabledProductIds ?? []).filter(Boolean)),
    [disabledProductIds],
  )
  const highlightedProductIdSet = useMemo(
    () => new Set((highlightedProductIds ?? []).filter(Boolean)),
    [highlightedProductIds],
  )
  const highlightedStockKeySet = useMemo(
    () => new Set((highlightedStockKeys ?? []).filter(Boolean)),
    [highlightedStockKeys],
  )
  const bulkSelectEnabled = multiSelect && !!onChooseMany

  // ── Selectability logic ────────────────────────────────────────────────

  /**
   * Determines whether a row is selectable and returns its visual state.
   * Blocking is quantity-based: a row is blocked when available ≤ 0 unless
   * the product is a service or allowOutOfStockSelection is true.
   */
  const getRowState = (row: PickerRow) => {
    const isLotDisabled = !!row.lotId && disabledLotIdSet.has(row.lotId)
    const isProductDisabled =
      !!row.productId && disabledProductIdSet.has(row.productId)
    const isHardDisabled = isLotDisabled || isProductDisabled

    const isOutOfStock =
      row.available <= 0 && !row.isService && !allowOutOfStockSelection

    const isSelectable = !isHardDisabled && !isOutOfStock
    const isPartiallyUsed = row.locally_reserved > 0

    return { isSelectable, isPartiallyUsed, isHardDisabled }
  }

  // ── Loading / error states ─────────────────────────────────────────────

  const isWarehousePending = !!normalizedWarehouseId && isStockPending
  const isWarehouseError = !!normalizedWarehouseId && isStockError
  const showTable = catalogOnly
    ? !isCatalogPending && !isCatalogError
    : !!normalizedWarehouseId && !isWarehousePending && !isWarehouseError

  const selectedSelections = useMemo(
    () => Object.values(selectedRowsByKey),
    [selectedRowsByKey],
  )

  const toggleRowSelection = (rowKey: string, payload: PickerSelection) => {
    setSelectedRowKeys((prev) => {
      const next = { ...prev }
      const isAlreadySelected = !!next[rowKey]
      if (isAlreadySelected) {
        delete next[rowKey]
      } else {
        next[rowKey] = true
      }
      return next
    })
    setSelectedRowsByKey((prev) => {
      const next = { ...prev }
      if (next[rowKey]) {
        delete next[rowKey]
      } else {
        next[rowKey] = payload
      }
      return next
    })
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
      initialFocusRef={searchInputRef}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {title ??
            (catalogOnly
              ? "Wybierz produkt"
              : "Wybierz produkt ze stanu magazynu")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!catalogOnly && !normalizedWarehouseId && (
            <Alert status="info" borderRadius="md" mb={3}>
              <AlertIcon />
              Najpierw wybierz magazyn.
            </Alert>
          )}

          {!catalogOnly && (
            <HStack p={2}>
              <Checkbox
                isChecked={inStockOnly}
                onChange={(e) => setInStockOnly(e.target.checked)}
                isDisabled={!normalizedWarehouseId}
              >
                na stanie
              </Checkbox>
            </HStack>
          )}

          {/* Search bar */}
          <HStack mb={3} align="stretch">
            <Input
              ref={searchInputRef}
              placeholder={
                catalogOnly
                  ? "Szukaj po SKU, nazwie, producencie…"
                  : "Szukaj po SKU, nazwie, jednostce…"
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              isDisabled={!catalogOnly && !normalizedWarehouseId}
            />
            <Button
              onClick={() => setQuery("")}
              isDisabled={!catalogOnly && !normalizedWarehouseId}
            >
              Wyczyść
            </Button>
          </HStack>

          {/* Loading spinner */}
          {catalogOnly ? (
            isCatalogPending && (
              <Flex justify="center" align="center" h="40vh">
                <Spinner />
              </Flex>
            )
          ) : isWarehousePending ? (
            <Flex justify="center" align="center" h="40vh">
              <Spinner />
            </Flex>
          ) : null}

          {/* Warehouse stock error */}
          {!catalogOnly && isWarehouseError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Nie udało się załadować stanów magazynowych:{" "}
              {(stockError as any)?.message ?? "Błąd"}
            </Alert>
          )}

          {!catalogOnly && !inStockOnly && isCatalogPending && (
            <HStack mb={3} color="gray.500">
              <Spinner size="sm" />
              <ChakraText>Ładowanie katalogu produktów…</ChakraText>
            </HStack>
          )}

          {catalogOnly && isCatalogError && (
            <Alert status="error" borderRadius="md" mb={3}>
              <AlertIcon />
              Nie udało się załadować katalogu produktów:{" "}
              {(catalogError as any)?.message ?? "Błąd"}
            </Alert>
          )}

          {!catalogOnly && !inStockOnly && isCatalogError && (
            <Alert status="error" borderRadius="md" mb={3}>
              <AlertIcon />
              Nie udało się załadować katalogu produktów:{" "}
              {(catalogError as any)?.message ?? "Błąd"}
            </Alert>
          )}

          {/* Table */}
          {showTable && (
            <TableContainer
              maxH="60vh"
              overflowY="auto"
              borderWidth="1px"
              borderRadius="md"
            >
              <Table
                size="sm"
                minW={bulkSelectEnabled ? "1460px" : "1420px"}
                sx={{ tableLayout: "fixed" }}
              >
                <Thead>
                  <Tr>
                    {bulkSelectEnabled && <Th w="48px" />}
                    <Th w="120px">SKU</Th>
                    <Th w="280px">Nazwa</Th>
                    <Th w="180px">Partia</Th>
                    <Th w="180px">Producent</Th>
                    <Th w="80px">Jm</Th>
                    <Th w="140px">Miejsce</Th>
                    <Th w="100px" whiteSpace="nowrap" isNumeric>
                      Na stanie
                    </Th>
                    <Th w="100px" whiteSpace="nowrap" isNumeric>
                      Rez. MM
                    </Th>
                    <Th w="100px" whiteSpace="nowrap" isNumeric>
                      Rez. SO
                    </Th>
                    <Th w="100px" whiteSpace="nowrap" isNumeric>
                      W dok.
                    </Th>
                    <Th w="110px" whiteSpace="nowrap" isNumeric>
                      Dostępne
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filtered.map((r) => {
                    const isCatalogRow = catalogOnly
                    const stockKey = isCatalogRow
                      ? undefined
                      : buildProductPickerStockKey(
                          r.productId,
                          r.location_code,
                          r.lotId,
                        )
                    const isHighlighted = isCatalogRow
                      ? !!r.productId &&
                        highlightedProductIdSet.has(r.productId)
                      : !!stockKey && highlightedStockKeySet.size > 0
                        ? highlightedStockKeySet.has(stockKey)
                        : !!r.productId &&
                          highlightedProductIdSet.has(r.productId)
                    const { isSelectable, isPartiallyUsed, isHardDisabled } =
                      isCatalogRow
                        ? {
                            isSelectable: true,
                            isPartiallyUsed: false,
                            isHardDisabled: false,
                          }
                        : getRowState(r)
                    const lotLabel = r.isWarehouseLot
                      ? (r.lotNo ?? r.lotId ?? "—")
                      : "—"

                    // One product may have several locations or produced lots.
                    const rowKey = isCatalogRow
                      ? r.productId
                      : (stockKey ?? r.productId)
                    const isSelected = !!selectedRowKeys[rowKey]
                    const selectionAvailableQty =
                      isCatalogRow || r.isService ? undefined : r.available
                    const selectionPayload: PickerSelection = {
                      productId: r.productId,
                      locationCode: r.location_code,
                      lotId: r.lotId,
                      lotNo: r.lotNo,
                      product: r.product,
                      productionDate: r.productionDate,
                      expirationDate: r.expirationDate,
                      availableQty: selectionAvailableQty,
                    }

                    return (
                      <Tr
                        key={rowKey}
                        bg={
                          isSelected
                            ? selectedRowBg
                            : isHardDisabled
                              ? selectedRowBg
                              : isPartiallyUsed && isSelectable
                                ? partiallyUsedRowBg
                                : undefined
                        }
                        _hover={isSelectable ? { bg: rowHoverBg } : undefined}
                        cursor={isSelectable ? "pointer" : "not-allowed"}
                        opacity={isSelectable ? 1 : 0.6}
                        onClick={() => {
                          if (!isSelectable) return
                          if (bulkSelectEnabled) {
                            toggleRowSelection(rowKey, selectionPayload)
                            return
                          }
                          onChoose(
                            r.productId,
                            r.location_code,
                            r.lotId,
                            r.lotNo,
                            r.product,
                            r.productionDate,
                            r.expirationDate,
                            selectionAvailableQty,
                          )
                        }}
                      >
                        {bulkSelectEnabled && (
                          <Td>
                            <Checkbox
                              isChecked={isSelected}
                              isDisabled={!isSelectable}
                              onChange={() =>
                                toggleRowSelection(rowKey, selectionPayload)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Td>
                        )}
                        <Td
                          whiteSpace="normal"
                          overflowWrap="anywhere"
                          wordBreak="break-word"
                        >
                          {r.sku}
                        </Td>
                        <Td maxW="280px" overflow="hidden">
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            minW={0}
                          >
                            <Tooltip
                              label={r.name}
                              placement="top"
                              hasArrow
                              openDelay={300}
                            >
                              <ChakraText
                                as="span"
                                display="block"
                                minW={0}
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                              >
                                {r.name}
                              </ChakraText>
                            </Tooltip>
                            {/* Badge: already present in the current form/document. */}
                            {isHighlighted && !isHardDisabled && (
                              <Badge colorScheme="blue" flexShrink={0}>
                                W dokumencie
                              </Badge>
                            )}
                            {/* Badge: hard-disabled product/lot */}
                            {!isCatalogRow && isHardDisabled && (
                              <Badge colorScheme="orange" flexShrink={0}>
                                Wybrana
                              </Badge>
                            )}
                            {/* Badge: partially used — still has available qty */}
                            {!isCatalogRow &&
                              !isHardDisabled &&
                              isPartiallyUsed &&
                              isSelectable && (
                                <Badge colorScheme="blue" flexShrink={0}>
                                  W dokumencie
                                </Badge>
                              )}
                            {/* Badge: out of stock — not selectable */}
                            {!isCatalogRow &&
                              !isHardDisabled &&
                              !isSelectable &&
                              !allowOutOfStockSelection && (
                                <Badge colorScheme="red" flexShrink={0}>
                                  Brak
                                </Badge>
                              )}
                          </Box>
                        </Td>
                        <Td maxW="180px" overflow="hidden">
                          <Tooltip
                            label={[
                              lotLabel,
                              r.productionDate
                                ? `Prod. ${formatLotDate(r.productionDate)}`
                                : "",
                              r.expirationDate
                                ? `Ważne do ${formatLotDate(r.expirationDate)}`
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" • ")}
                            placement="top"
                            hasArrow
                            openDelay={300}
                          >
                            <Box>
                              <ChakraText
                                display="block"
                                overflow="hidden"
                                textOverflow="ellipsis"
                                whiteSpace="nowrap"
                              >
                                {lotLabel}
                              </ChakraText>
                              {r.productionDate && (
                                <ChakraText fontSize="xs" color="gray.500">
                                  Prod. {formatLotDate(r.productionDate)}
                                </ChakraText>
                              )}
                              {r.expirationDate && (
                                <ChakraText fontSize="xs" color="orange.600">
                                  Ważne do {formatLotDate(r.expirationDate)}
                                </ChakraText>
                              )}
                            </Box>
                          </Tooltip>
                        </Td>
                        <Td maxW="180px" overflow="hidden">
                          <Tooltip
                            label={r.manufacturer || "—"}
                            placement="top"
                            hasArrow
                            openDelay={300}
                          >
                            <ChakraText
                              display="block"
                              overflow="hidden"
                              textOverflow="ellipsis"
                              whiteSpace="nowrap"
                            >
                              {r.manufacturer || "—"}
                            </ChakraText>
                          </Tooltip>
                        </Td>
                        <Td>{r.unit}</Td>
                        <Td>{r.location_code || "—"}</Td>
                        <Td isNumeric>{r.qty_on_hand}</Td>
                        <Td isNumeric>{r.reserved_mm}</Td>
                        <Td isNumeric>{r.reserved_so}</Td>
                        <Td isNumeric>{r.locally_reserved}</Td>
                        <Td isNumeric>{r.isService ? "—" : r.available}</Td>
                      </Tr>
                    )
                  })}

                  {/* Empty state */}
                  {filtered.length === 0 &&
                    !(!inStockOnly && isCatalogPending) && (
                      <Tr>
                        <Td colSpan={bulkSelectEnabled ? 12 : 11}>
                          <Box p={2} color="gray.500">
                            {normalizedQuery
                              ? "Brak wyników"
                              : "Brak produktów do wyświetlenia"}
                          </Box>
                        </Td>
                      </Tr>
                    )}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Zamknij
          </Button>
          {bulkSelectEnabled && (
            <Button
              ml={3}
              colorScheme="green"
              onClick={() => {
                if (!onChooseMany) return
                onChooseMany(selectedSelections)
              }}
              isDisabled={selectedSelections.length === 0}
            >
              Dodaj{" "}
              {selectedSelections.length > 0
                ? `(${selectedSelections.length})`
                : ""}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
